import { useRef, useState, useEffect } from "react";
import { Play, Pause, Download, Volume2, VolumeX } from "lucide-react";
import { SET_PLAYING } from "@/redux/features/music/musicSlice";
import { useDispatch, useSelector } from "react-redux";
import { SET_SHOW_LOGIN } from "@/redux/features/auth/authSlice";
import toast from "react-hot-toast";
import axios from "axios";

export default function VideoPlayer({ video }) {
  const videoRef = useRef(null);
  const controlsTimeoutRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [progress, setProgress] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1.0);
  const [mute, setMute] = useState(false);
  const [isSeeking, setIsSeeking] = useState(false);
  const [wasPlayingBeforeSeek, setWasPlayingBeforeSeek] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showControls, setShowControls] = useState(false);
  const animateRef = useRef();
  const dispatch = useDispatch();
  const { user } = useSelector((state) => state.auth);
  const [downloadLoading, setdownloadLoading] = useState(false);

  const togglePlay = () => {
    const videoElement = videoRef.current;
    if (videoElement.paused) {
      videoElement.play();
      setIsPlaying(true);
      dispatch(SET_PLAYING(false));
    } else {
      videoElement.pause();
      setIsPlaying(false);
    }
  };

  const handleTimeUpdate = () => {
    const videoElement = videoRef.current;
    setProgress(videoElement.currentTime);
  };

  const handleLoadedMetadata = () => {
    const videoElement = videoRef.current;
    setDuration(videoElement.duration);
  };

  const handleLoadedData = () => {
    setIsLoading(false);
  };

  const handleWaiting = () => {
    setIsLoading(true);
  };

  const handlePlaying = () => {
    setIsLoading(false);
  };

  const handleMouseDownSeek = (e) => {
    e.stopPropagation();
    setWasPlayingBeforeSeek(isPlaying);
    if (isPlaying) {
      const videoElement = videoRef.current;
      videoElement.pause();
      setIsPlaying(false);
    }
    setIsSeeking(true);
  };

  const handleMouseUpSeek = (e) => {
    e.stopPropagation();
    setIsSeeking(false);
    const videoElement = videoRef.current;
    videoElement.currentTime = parseFloat(e.target.value);
    setProgress(parseFloat(e.target.value));

    if (wasPlayingBeforeSeek) {
      videoElement.play();
      setIsPlaying(true);
    }
  };

  const handleSeekingChange = (e) => {
    e.stopPropagation();
    const newSeek = parseFloat(e.target.value);
    setProgress(newSeek);
    if (isSeeking) {
      videoRef.current.currentTime = newSeek;
    }
  };

  const handleVolumeChange = (e) => {
    e.stopPropagation();
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
    videoRef.current.volume = newVolume;
    if (newVolume === 0) {
      setMute(true);
    } else {
      setMute(false);
    }
  };

  const toggleMute = (e) => {
    e.stopPropagation();
    const videoElement = videoRef.current;
    videoElement.muted = !mute;
    setMute(!mute);
  };

  // download video
  const downloadVideo = async () => {
    if (!user || user?.is_guest) {
      toast.error('Login to download the video');
      dispatch(SET_SHOW_LOGIN(true));
      return;
    }
    const token = window.localStorage.getItem('token');
    try {
      setdownloadLoading(true);
      toast.loading("Starting Download", { id: "download" });
      const match = video.match(/\/(\d+)\//);
      const songId = match ? match[1] : null;
      const response = await axios.get(`/videostatus?song_id=${songId}`, { headers: { Authorization: `Bearer ${token}` } });
      const videoUrl = response.data.url;
      console.log(videoUrl);
      download(videoUrl, `${video.title || 'video'} - Sound of Meme.mp4`);
      setdownloadLoading(false);
      toast.success("Download Started", { id: "download" });
    } catch (error) {
      setdownloadLoading(false);
      console.error('Error downloading video:', error);
      toast.error('Error occurred. Please try again.', { id: "download" });
    }
  };

  function download(url, filename) {
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  }

  const formatTime = (timeInSeconds) => {
    const minutes = Math.floor(timeInSeconds / 60);
    const seconds = Math.floor(timeInSeconds % 60);
    return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  };

  const progressPercent = (progress / duration) * 100;
  const volumePercent = (volume / 1) * 100;

  const handleMouseMove = () => {
    setShowControls(true);

    // Clear existing timeout
    if (controlsTimeoutRef.current) {
      clearTimeout(controlsTimeoutRef.current);
    }

    // Set new timeout to hide controls after 3 seconds
    controlsTimeoutRef.current = setTimeout(() => {
      if (!isSeeking) {
        setShowControls(false);
      }
    }, 3000);
  };

  const handleMouseLeave = () => {
    if (!isSeeking) {
      setShowControls(false);
    }
  };

  useEffect(() => {
    if (isPlaying) {
      animateRef.current?.play();
      animateRef.current?.setSpeed(1.8);
    } else {
      animateRef.current?.pause();
    }

    // Clean up timeout on unmount
    return () => {
      if (controlsTimeoutRef.current) {
        clearTimeout(controlsTimeoutRef.current);
      }
    };
  }, [isPlaying]);

  return (
    <div
      className="w-full max-w-sm mx-auto rounded-md overflow-hidden relative transition-all duration-300"
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      onTouchStart={handleMouseMove}
    >
      <div className="relative aspect-[6/9]">
        <video
          ref={videoRef}
          className={`w-full h-full object-cover transition-all duration-300 ${isLoading ? 'opacity-60 blur-[2px]' : ''}`}
          src={video}
          onTimeUpdate={handleTimeUpdate}
          onLoadedMetadata={handleLoadedMetadata}
          onLoadedData={handleLoadedData}
          onWaiting={handleWaiting}
          onPlaying={handlePlaying}
          onClick={togglePlay}
          type="video/mp4"
          muted={mute}
        />

        {isLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/40 backdrop-blur-sm z-10">
            <div className="w-14 h-14 border-t-4 border-r-4 border-green-400 dark:border-pink-500 border-solid rounded-full animate-spin"></div>
          </div>
        )}

        {!isPlaying && !isLoading && (
          <div className="absolute inset-0 flex items-center justify-center backdrop-blur-[1px]">
            <button
              onClick={togglePlay}
              className="bg-green-400/70 dark:bg-pink-600/70 hover:bg-green-400 dark:hover:bg-pink-600 rounded-full p-5 z-10 transition-all duration-300 transform hover:scale-110 shadow-lg hover:shadow-green-400/30 dark:hover:shadow-pink-600/30"
            >
              <Play className="pl-1 fill-white" size={36} />
            </button>
          </div>
        )}

        {/* Overlay gradient for better control visibility */}
        <div className={`absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-black/90 to-transparent transition-opacity duration-500 ease-in-out ${showControls ? 'opacity-100' : 'opacity-0'}`}></div>

        {/* Controls container */}
        <div className={`absolute bottom-0 left-0 right-0 p-5 transition-all duration-500 ease-in-out transform ${showControls ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}>
          {/* Progress bar */}
          <div className="w-full max-w-full relative group mb-4">
            <input
              type="range"
              min="0"
              max={duration || 0}
              step="0.01"
              value={progress}
              onChange={handleSeekingChange}
              onMouseDown={handleMouseDownSeek}
              onMouseUp={handleMouseUpSeek}
              onTouchStart={handleMouseDownSeek}
              onTouchEnd={handleMouseUpSeek}
              className="w-full appearance-none rounded-full h-7 cursor-pointer opacity-0 absolute -top-3 left-0 z-20"
            />
            <div className="w-full h-1.5 bg-white/20 rounded-full transition-all duration-300 group-hover:h-2">
              <div
                style={{ width: `${progressPercent}%` }}
                className="h-full bg-gradient-to-r from-green-400 to-emerald-500 dark:from-pink-600 dark:to-purple-500 rounded-full relative transition-all duration-300"
              >
                <div className="absolute right-0 top-1/2 -translate-y-1/2 w-4 h-4 bg-white rounded-full opacity-0 group-hover:opacity-100 transform translate-x-1/2 shadow-md transition-all duration-200 scale-90 group-hover:scale-100"></div>
              </div>
            </div>
            <div className="flex justify-between text-xs text-white/80 font-medium mt-1.5">
              <span>{formatTime(Math.round(progress))}</span>
              <span>{duration ? formatTime(Math.round(duration)) : "00:00"}</span>
            </div>
          </div>

          {/* Control buttons */}
          <div className="w-full flex justify-between items-center">
            <div className="flex items-center gap-5">
              <button
                onClick={togglePlay}
                className="text-white hover:text-green-400 dark:hover:text-pink-400 transition-colors duration-200 transform hover:scale-110"
              >
                {isPlaying ? <Pause size={24} /> : <Play className="pl-0.5" size={24} />}
              </button>

              <div className="flex items-center gap-3">
                <button
                  onClick={toggleMute}
                  className="text-white hover:text-green-400 dark:hover:text-pink-400 transition-colors duration-200 transform hover:scale-110"
                >
                  {mute ? <VolumeX size={20} /> : <Volume2 size={20} />}
                </button>

                <div className="relative w-20 hidden sm:block group">
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.05"
                    value={volume}
                    onChange={handleVolumeChange}
                    className="w-full appearance-none rounded-full h-5 cursor-pointer opacity-0 absolute -top-2 left-0 z-10"
                  />
                  <div className="w-full h-1.5 bg-white/20 rounded-full transition-all duration-300">
                    <div
                      style={{ width: `${volumePercent}%` }}
                      className="h-full bg-white/80 group-hover:bg-green-400 dark:group-hover:bg-pink-500 rounded-full transition-all duration-300"
                    ></div>
                  </div>
                </div>
              </div>
            </div>

            <button
              onClick={downloadVideo}
              disabled={downloadLoading}
              className="text-white/90 hover:text-green-400 dark:hover:text-pink-400 transition-colors duration-200 transform hover:scale-110"
            >
              <Download size={20} />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}